// src/pages/User/NewRequest/ui/steps/purchase/PurchaseStepDesktop.tsx
import React, { useMemo } from "react";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { format } from "date-fns";

import CalendarSpecific from "@components/CalendarSpecific";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { CollapsibleSectionCard } from "../../components/CollapsibleSectionCard";
import { PurchasePreview } from "./PurchasePreview";

import type { CustomerOptions } from "../../../model/customer.model";
import { toFreightSafe } from "../../../domain/policies/freightPolicy";
import { usePurchaseStep } from "../../hook/usePurchaseStep";
import {
  labelSx,
  disabledFieldSx,
  disabledSelectSx,
  disabledCalendarSx,
} from "./styles";

function onlyDigits(v: string): string {
  return v.replace(/\D/g, "");
}

export function PurchaseStepDesktop() {
  const {
    canInteract,
    isLoading,
    error,

    companies,
    customer,
    availableDates,
    freightPolicy,

    values,
    setFieldValue,

    handleCustomerChange,
    handleCustomerInput,
    onChangeDeliveryAddress,
    onChangeWithdrawBasis,
    onChangeFreightType,
  } = usePurchaseStep();

  const basisOptions = customer?.withdrawBasis ?? [];
  const addressOptions = customer?.addresses ?? [];

  // fonte da verdade: Formik
  const selectedCompany = useMemo<CustomerOptions | null>(() => {
    const cnpj = onlyDigits(values.customerCnpj ?? "");
    if (!cnpj) return null;
    return companies.find((c) => onlyDigits(c.key) === cnpj) ?? null;
  }, [companies, values.customerCnpj]);

  const disableAll = isLoading;
  const disableDerived = disableAll || !canInteract;

  const isAddressDisabled = disableDerived || !addressOptions.length;
  const isBasisDisabled = disableDerived || !basisOptions.length;
  const hasNoDates = !availableDates || !Object.keys(availableDates).length;
  const isCalendarDisabled = disableDerived || hasNoDates;

  const purchasePreview = values.customerCnpj ? (
    <PurchasePreview
      customerCnpj={values.customerCnpj}
      selectedCompany={selectedCompany}
      customer={customer}
      deliveryAddressId={values.deliveryAddressId}
      withdrawBasisId={values.withdrawBasisId}
      deliveryDate={values.deliveryDate}
      freightType={values.freightType}
    />
  ) : null;

  return (
    <Box sx={{ position: "relative" }}>
      <LoadingOverlay open={isLoading} />

      <CollapsibleSectionCard
        title="Dados da compra"
        defaultExpanded
        preview={purchasePreview}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Autocomplete<CustomerOptions, false, false, false>
              options={companies}
              value={selectedCompany}
              onChange={(_, opt) => handleCustomerChange(opt)}
              onInputChange={(_, inputValue, reason) =>
                handleCustomerInput({ inputValue, reason })
              }
              getOptionLabel={(opt) => opt.value}
              isOptionEqualToValue={(a, b) =>
                onlyDigits(a.key) === onlyDigits(b.key)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Razão social"
                  placeholder="Selecione uma empresa"
                  fullWidth
                  disabled={disableAll}
                  error={Boolean(error)}
                  helperText={error ?? " "}
                  sx={labelSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              disabled={isAddressDisabled}
              sx={isAddressDisabled ? disabledSelectSx : labelSx}
            >
              <InputLabel id="addr">Endereço de entrega</InputLabel>
              <Select
                labelId="addr"
                label="Endereço de entrega"
                value={values.deliveryAddressId}
                onChange={(e) =>
                  onChangeDeliveryAddress(String(e.target.value))
                }
              >
                {addressOptions.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.street}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="CEP"
              value={values.postalCode}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Bairro"
              value={values.district}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Cidade e estado"
              value={values.cityState}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={2}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <FormControl disabled={disableAll || freightPolicy.locked}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 0.5,
                  fontWeight: "bold",
                  color: "common.black",
                  fontSize: "1.5rem",
                }}
              >
                Frete:
              </Typography>

              <RadioGroup
                row
                value={values.freightType}
                onChange={(e) =>
                  onChangeFreightType(toFreightSafe(e.target.value))
                }
              >
                {freightPolicy.allowed.map((ft) => (
                  <FormControlLabel
                    key={ft}
                    value={ft}
                    control={<Radio />}
                    label={ft}
                  />
                ))}
              </RadioGroup>

              {freightPolicy.locked && freightPolicy.reason ? (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, opacity: 0.8 }}
                >
                  {freightPolicy.reason}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              disabled={isBasisDisabled}
              sx={isBasisDisabled ? disabledSelectSx : labelSx}
            >
              <InputLabel id="basis">Base de retirada</InputLabel>
              <Select
                labelId="basis"
                label="Base de retirada"
                value={values.withdrawBasisId}
                onChange={(e) => onChangeWithdrawBasis(String(e.target.value))}
              >
                {basisOptions.map((b) => (
                  <MenuItem key={b.shippingPoint} value={b.shippingPoint}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Filial"
              value={values.branchName}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={isCalendarDisabled ? disabledCalendarSx : undefined}>
              <CalendarSpecific
                id="dateDelivery"
                name="dateDelivery"
                label="Data da entrega"
                value={values.deliveryDate}
                onChange={(date: Date | null) => {
                  const iso = date ? format(date, "yyyy-MM-dd'T'00:00:00") : "";
                  setFieldValue("purchase.deliveryDate", iso, false);
                }}
                onBlur={undefined}
                allowedDatesMap={availableDates}
                disabled={isCalendarDisabled}
              />
            </Box>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
          Dados do responsável
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Assessor"
              value={values.responsibleName ?? ""}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Telefone (+DDD)"
              value={values.responsiblePhone ?? ""}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="E-mail"
              value={values.responsibleEmail ?? ""}
              fullWidth
              disabled
              sx={disabledFieldSx}
            />
          </Grid>
        </Grid>
      </CollapsibleSectionCard>
    </Box>
  );
}
